'use client';

/**
 * Paginator URL-backed pour les listings catalog.
 *
 * Wrapper Next-routing autour du `Pagination` présentationnel de
 * `@odys/design-system` : il branche le routing (next/link + searchParams)
 * et délègue tout le rendu au composant partagé.
 *
 * - Page courante lue depuis `usePageQueryParam` — toute mise à jour passe
 *   par `router.replace({ scroll: false })`.
 * - Chaque item est un `next/link` (`{ scroll: false, replace: true }`) → vrais
 *   `<a href>` (back/forward natif, partage de lien, open-in-new-tab OK).
 * - Hidden quand `count <= 1`, `loading` = busy : gérés par le composant partagé.
 */

import { Pagination } from '@odys/design-system/components/Pagination';
import NextLink from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

import { usePageQueryParam } from './usePageQueryParam';

export type UrlPaginationProps = {
    /** Total number of pages. The paginator hides itself when `count <= 1`. */
    count: number;
    /** Optional override of the search param key (defaults to `page`). */
    paramKey?: string;
    /** Show a subtle busy indicator while the next page is loading. */
    loading?: boolean;
    /** Optional `data-testid` to scope tests to a specific paginator. */
    'data-testid'?: string;
};

export function UrlPagination({
    count,
    paramKey = 'page',
    loading = false,
    'data-testid': testId,
}: UrlPaginationProps): React.JSX.Element | null {
    const { page, setPage } = usePageQueryParam(paramKey);
    const searchParams = useSearchParams();

    const buildHref = useCallback(
        (targetPage: number): string => {
            const params = new URLSearchParams(searchParams.toString());
            if (targetPage <= 1) {
                params.delete(paramKey);
            } else {
                params.set(paramKey, String(targetPage));
            }
            const query = params.toString();
            return query.length > 0 ? `?${query}` : '?';
        },
        [searchParams, paramKey],
    );

    return (
        <Pagination
            count={count}
            page={page}
            getItemHref={buildHref}
            onChange={setPage}
            LinkComponent={NextLink}
            linkProps={{ scroll: false, replace: true }}
            loading={loading}
            data-testid={testId ?? 'url-pagination'}
        />
    );
}
